
import { supabase } from "../lib/supabase";

const PYTHON_BACKEND_URL = import.meta.env.VITE_PYTHON_BACKEND_URL || 'http://localhost:8000';

/**
 * Fire-and-forget: triggers the draft comparison agent if counselor feedback exists.
 * Called after createDraft succeeds. Never throws — comparison failure must not block the save.
 */
async function _maybeTriggerDraftComparison(
  essayId: string,
  newDraftId: string,
  studentId: string
): Promise<void> {
  try {
    // Check if any counselor feedback exists for this essay
    const { data } = await supabase
      .from('counselor_essay_feedback')
      .select('id')
      .eq('essay_id', essayId)
      .limit(1);

    if (!data || data.length === 0) return; // No feedback yet — skip

    fetch(`${PYTHON_BACKEND_URL}/api/essay/compare-drafts`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ essay_id: essayId, new_draft_id: newDraftId, student_id: studentId }),
    }).catch((err) => console.warn('[DraftComparison] Background call failed:', err));
  } catch (err) {
    console.warn('[DraftComparison] Pre-check failed:', err);
  }
}

/**
 * Student requests counselor re-review after editing in-place.
 * Triggers the draft comparison agent using the current essay content (no new draft needed).
 * Fire-and-forget — never throws.
 */
export async function requestReview(essayId: string, studentId: string): Promise<void> {
  const { data } = await supabase
    .from('counselor_essay_feedback')
    .select('id')
    .eq('essay_id', essayId)
    .limit(1);

  if (!data || data.length === 0) return; // No counselor feedback yet

  await fetch(`${PYTHON_BACKEND_URL}/api/essay/compare-drafts`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ essay_id: essayId, student_id: studentId }),
  });
}

export interface EssayDB {
  id: string;
  student_id: string;
  title: string;
  is_starred: boolean;
  created_at: string;
  updated_at: string;
  college_id?: string;
  essay_type_id?: number;
  prompt_id?: string;
  is_complete?: boolean;
}

export interface DraftDB {
  id: string;
  essay_id: string;
  draft_number: number;
  content: string;
  title: string;
  word_count: number;
  created_at: string;
  updated_at: string;
}

export interface SupplementalPromptDB {
  id: string; // UUID after migration
  college_name: string;
  college_prompt_id: string; // String identifier like "princeton_1" (renamed from prompt_id)
  prompt_text: string;
  Requirement_Type: string | null;
  Condition_Rule: string | null;
  Group_ID: string | null;
  word_min: number | null;
  word_max: number | null;
  Note: string | null;
  url: string | null;
}

// ==========================================
// ESSAY OPERATIONS
// ==========================================

/**
 * Create a new essay with an initial draft
 */
export async function createEssay(
  studentId: string,
  title: string,
  collegeId?: string,
  essayTypeId?: number,
  promptId?: string,
  updatedAt?: string,
) {
  // 1. Create the Essay (Topic)
  const { data: essay, error: essayError } = await supabase
    .from("essays")
    .insert([
      {
        student_id: studentId,
        title: title,
        college_id: collegeId || null,
        essay_type_id: essayTypeId || null,
        prompt_id: promptId || null,
        is_starred: false,
        updated_at: updatedAt || new Date().toISOString(),
      }
    ])
    .select()
    .single();

  if (essayError) throw essayError;

  // 2. Create the Initial Draft (Draft 1)
  const { data: draft, error: draftError } = await supabase
    .from("essay_drafts")
    .insert([
      {
        essay_id: essay.id,
        draft_number: 1,
        content: "",
        word_count: 0,
      },
    ])
    .select()
    .single();

  if (draftError) throw draftError;

  return { essay, draft };
}

/**
 * Get all essays for a student
 */
export async function getEssays(studentId: string) {
  const { data, error } = await supabase
    .from("essays")
    .select("*")
    .eq("student_id", studentId)
    .order("updated_at", { ascending: false });

  if (error) throw error;
  return data;
}

/**
 * Get a single essay by ID
 */
export async function getEssay(essayId: string) {
  const { data, error } = await supabase
    .from("essays")
    .select("*")
    .eq("id", essayId)
    .single();

  if (error) throw error;
  return data;
}

/**
 * Update essay title
 */
export async function updateEssayTitle(essayId: string, title: string) {
  const { data, error } = await supabase
    .from("essays")
    .update({ title, updated_at: new Date().toISOString() })
    .eq("id", essayId)
    .select()
    .single();

  if (error) throw error;
  return data;
}



/**
 * Toggle essay starred status
 */
export async function toggleEssayStar(
  essayId: string,
  isStarred: boolean
): Promise<EssayDB> {

  const { data, error } = await supabase
    .from("essays")
    .update({ is_starred: isStarred })
    .eq("id", essayId)
    .select()
    .single();

  if (error) {
    console.error("🔧 [API] toggleEssayStar error:", error);
    throw error;
  }

  return data as EssayDB;
}

/**
 * Delete an essay (cascades to drafts, conversations, messages)
 */
export async function deleteEssay(essayId: string): Promise<void> {

  const { error } = await supabase.from("essays").delete().eq("id", essayId);

  if (error) {
    console.error("🔧 [API] deleteEssay error:", error);
    throw error;
  }

}

// ==========================================
// DRAFT OPERATIONS

/**
 * Get a single draft by ID
 */
export async function getDraft(draftId: string): Promise<DraftDB> {

  const { data, error } = await supabase
    .from("essay_drafts")
    .select("*")
    .eq("id", draftId)
    .single();

  if (error) {
    console.error("🔧 [API] getDraft error:", error);
    throw error;
  }

  return data as DraftDB;
}

/**
 * Create a new draft for an essay
 */
export async function createDraft(essayId: string, studentId?: string): Promise<DraftDB> {

  // Get the current highest draft number
  const { data: existingDrafts, error: fetchError } = await supabase
    .from("essay_drafts")
    .select("draft_number")
    .eq("essay_id", essayId)
    .order("draft_number", { ascending: false })
    .limit(1);

  if (fetchError) {
    console.error("🔧 [API] createDraft fetch error:", fetchError);
    throw fetchError;
  }

  const nextDraftNumber =
    existingDrafts && existingDrafts.length > 0
      ? existingDrafts[0].draft_number + 1
      : 1;

  // Create the new draft
  const { data, error } = await supabase
    .from("essay_drafts")
    .insert([
      {
        essay_id: essayId,
        draft_number: nextDraftNumber,
        content: "",
        title: `Draft ${nextDraftNumber} `,
      },
    ])
    .select()
    .single();

  if (error) {
    console.error("🔧 [API] createDraft error:", error);
    throw error;
  }

  // Trigger comparison agent in background if counselor feedback exists
  if (studentId) {
    _maybeTriggerDraftComparison(essayId, data.id, studentId);
  }

  return data as DraftDB;
}

/**
 * Update draft content
 */
export async function updateDraftContent(
  draftId: string,
  content: string,
  wordCount: number = 0
): Promise<DraftDB> {
  const { data, error } = await supabase
    .from("essay_drafts")
    .update({
      content,
      word_count: wordCount,
      updated_at: new Date().toISOString()
    })
    .eq("id", draftId)
    .select()
    .single();

  if (error) {
    console.error("🔧 [API] updateDraftContent error:", error);
    throw error;
  }

  // Also update the parent essay's updated_at timestamp
  const { error: essayUpdateError } = await supabase
    .from("essays")
    .update({ updated_at: new Date().toISOString() })
    .eq("id", data.essay_id);

  if (essayUpdateError) {
    console.error("🔧 [API] updateDraftContent essay update error:", essayUpdateError);
  }

  return data as DraftDB;
}

/**
 * Get all drafts for an essay
 */
export async function getDrafts(essayId: string): Promise<DraftDB[]> {
  const { data, error } = await supabase
    .from("essay_drafts")
    .select("*")
    .eq("essay_id", essayId)
    .order("draft_number", { ascending: true });

  if (error) throw error;
  return data as DraftDB[];
}

/**
 * Update draft title and content
 */
export async function updateDraft(
  draftId: string,
  updates: { title?: string; content?: string; word_count?: number }
): Promise<DraftDB> {
  const { data, error } = await supabase
    .from("essay_drafts")
    .update({ ...updates, updated_at: new Date().toISOString() })
    .eq("id", draftId)
    .select()
    .single();

  if (error) throw error;

  // Also update the parent essay's updated_at timestamp
  const { error: essayUpdateError } = await supabase
    .from("essays")
    .update({ updated_at: new Date().toISOString() })
    .eq("id", data.essay_id);

  if (essayUpdateError) {
    console.error("🔧 [API] updateDraft essay update error:", essayUpdateError);
  }

  return data as DraftDB;
}

/**
 * Delete a draft
 */
export async function deleteDraft(draftId: string): Promise<void> {

  const { error } = await supabase
    .from("essay_drafts")
    .delete()
    .eq("id", draftId);

  if (error) {
    console.error("🔧 [API] deleteDraft error:", error);
    throw error;
  }

}

// ==========================================
// ESSAY + DRAFT COUNT (for list view)
// ==========================================

/**
 * Get essays with draft counts
 */
export async function getEssaysWithDraftCount(
  studentId: string
): Promise<Array<EssayDB & { draft_count: number }>> {

  // First get all essays
  const essays = await getEssays(studentId);

  // Then get draft counts for each essay
  const essaysWithCounts = await Promise.all(
    essays.map(async (essay) => {
      const { count, error } = await supabase
        .from("essay_drafts")
        .select("*", { count: "exact", head: true })
        .eq("essay_id", essay.id);

      if (error) {
        console.error("🔧 [API] getDraftCount error:", error);
        return { ...essay, draft_count: 0 };
      }

      return { ...essay, draft_count: count || 0 };
    })
  );

  console.log(
    "🔧 [API] getEssaysWithDraftCount success:",
    essaysWithCounts.length,
    "essays"
  );
  return essaysWithCounts;
}

export async function getOrCreateEssay(
  studentId: string,
  collegeId: string,
  promptId: string,
  title: string,
  essayTypeId: number
) {
  // 1. Try to find existing essay
  const { data: existingEssays, error: fetchError } = await supabase
    .from("essays")
    .select("*")
    .eq("student_id", studentId)
    .eq("college_id", collegeId)
    .eq("prompt_id", promptId)
    .limit(1);

  if (fetchError) throw fetchError;

  if (existingEssays && existingEssays.length > 0) {
    return existingEssays[0];
  }

  // 2. If not found, create new essay
  const { essay } = await createEssay(studentId, title, collegeId, essayTypeId, promptId);
  return essay;
}

// ==========================================
// STUDENT OPERATIONS
// ==========================================

/**
 * Get the current logged-in student's ID from the students table
 * Returns students.id (not auth.uid())
 */
export async function getCurrentStudentId(): Promise<string | null> {
  // Get the authenticated user
  const { data: { user }, error: authError } = await supabase.auth.getUser();

  if (authError || !user) {
    console.error("❌ [API] Error getting auth user:", authError);
    return null;
  }

  // Get the student record that matches this auth user
  const { data: student, error: studentError } = await supabase
    .from("students")
    .select("id")
    .eq("auth_user_id", user.id)
    .single();

  if (studentError) {
    console.error("❌ [API] Error getting student:", studentError);
    return null;
  }

  return student?.id || null;
}

// ==========================================
// SUPPLEMENTAL PROMPTS
// ==========================================

/**
 * Get all supplemental prompts
 */
export async function getSupplementalPrompts(): Promise<SupplementalPromptDB[]> {
  const { data, error } = await supabase
    .from("college_supplemental_essays")
    .select("*");

  if (error) throw error;
  return data as SupplementalPromptDB[];
}

/**
 * Get supplemental prompts by college name
 */
export async function getPromptsByCollegeName(collegeName: string): Promise<SupplementalPromptDB[]> {
  const { data, error } = await supabase
    .from("college_supplemental_essays")
    .select("*")
    .eq("college_name", collegeName);

  if (error) throw error;
  return data as SupplementalPromptDB[];
}

/**
 * Get a single prompt by college_prompt_id
 */
export async function getSupplementalPromptByPromptId(promptId: string): Promise<SupplementalPromptDB | null> {
  const { data, error } = await supabase
    .from("college_supplemental_essays")
    .select("*")
    .eq("college_prompt_id", promptId)
    .single();

  if (error) {
    if (error.code === "PGRST116") return null; // Not found
    throw error;
  }
  return data as SupplementalPromptDB;
}

/**
 * Get the UUID id for a prompt by its college_prompt_id string
 * Converts "princeton_1" -> UUID from database
 */
export async function getPromptUuidByPromptId(promptId: string): Promise<string | null> {
  const { data, error } = await supabase
    .from("college_supplemental_essays")
    .select("id")
    .eq("college_prompt_id", promptId)
    .single();

  if (error) {
    console.error("❌ [API] Error fetching prompt UUID:", error);
    return null;
  }

  return data?.id || null;
}

// ==========================================
// COLLEGE QUESTIONNAIRE RESPONSES
// ==========================================

export interface CollegeQuestionnaireResponseDB {
  id: string;
  student_id: string;
  college_id: number;
  college_name: string;
  responses: Record<string, string | boolean>; // JSONB
  include_optional: boolean;
  include_conditional: boolean;
  created_at: string;
  updated_at: string;
}

/**
 * Get questionnaire responses for a student and college
 */
export async function getCollegeQuestionnaireResponse(
  studentId: string,
  collegeId: number
): Promise<CollegeQuestionnaireResponseDB | null> {
  const { data, error } = await supabase
    .from("college_questionnaire_responses")
    .select("*")
    .eq("student_id", studentId)
    .eq("college_id", collegeId)
    .single();

  if (error) {
    if (error.code === "PGRST116") return null; // Not found
    console.error("❌ [API] Error fetching questionnaire response:", error);
    return null;
  }

  return data as CollegeQuestionnaireResponseDB;
}

/**
 * Get all questionnaire responses for a student
 */
export async function getAllCollegeQuestionnaireResponses(
  studentId: string
): Promise<CollegeQuestionnaireResponseDB[]> {
  const { data, error } = await supabase
    .from("college_questionnaire_responses")
    .select("*")
    .eq("student_id", studentId);

  if (error) {
    console.error("❌ [API] Error fetching questionnaire responses:", error);
    return [];
  }

  return data as CollegeQuestionnaireResponseDB[];
}

/**
 * Save or update questionnaire responses for a student and college
 */
export async function saveCollegeQuestionnaireResponse(
  studentId: string,
  collegeId: number,
  collegeName: string,
  responses: Record<string, string | boolean>,
  includeOptional: boolean,
  includeConditional: boolean
): Promise<CollegeQuestionnaireResponseDB> {
  // Try to update existing record first
  const { data: existing, error: fetchError } = await supabase
    .from("college_questionnaire_responses")
    .select("id")
    .eq("student_id", studentId)
    .eq("college_id", collegeId)
    .single();

  if (existing) {
    // Update existing record
    const { data, error } = await supabase
      .from("college_questionnaire_responses")
      .update({
        responses,
        include_optional: includeOptional,
        include_conditional: includeConditional,
        updated_at: new Date().toISOString(),
      })
      .eq("id", existing.id)
      .select()
      .single();

    if (error) throw error;
    return data as CollegeQuestionnaireResponseDB;
  } else {
    // Create new record
    const { data, error } = await supabase
      .from("college_questionnaire_responses")
      .insert({
        student_id: studentId,
        college_id: collegeId,
        college_name: collegeName,
        responses,
        include_optional: includeOptional,
        include_conditional: includeConditional,
      })
      .select()
      .single();

    if (error) throw error;
    return data as CollegeQuestionnaireResponseDB;
  }
}

/**
 * Delete questionnaire responses for a student and college
 */
export async function deleteCollegeQuestionnaireResponse(
  studentId: string,
  collegeId: number
): Promise<void> {
  const { error } = await supabase
    .from("college_questionnaire_responses")
    .delete()
    .eq("student_id", studentId)
    .eq("college_id", collegeId);

  if (error) throw error;
}

/**
 * Toggle essay completion status
 */
export async function toggleEssayComplete(
  essayId: string,
  isComplete: boolean
): Promise<void> {
  const { error } = await supabase
    .from("essays")
    .update({ is_complete: isComplete, updated_at: new Date().toISOString() })
    .eq("id", essayId);

  if (error) {
    console.error("🔧 [API] toggleEssayComplete error:", error);
    throw error;
  }
}

/**
 * Get supplement progress stats for dashboard
 */
export async function getStudentSupplementsProgress(studentId: string) {
  // Get all supplement essays (type 3)
  const { data: essays, error } = await supabase
    .from("essays")
    .select("id, is_complete")
    .eq("student_id", studentId)
    .eq("essay_type_id", 3);

  if (error) {
    console.error("🔧 [API] getStudentSupplementsProgress error:", error);
    return { completed: 0, total: 0 };
  }

  const completed = essays?.filter(e => e.is_complete).length || 0;
  const total = essays?.length || 0; // This is total *started* supplements. 
  // Ideally total should be all required prompts for favorite colleges, but for now this is a start.

  return { completed, total };
}

export interface SupplementProgress {
  collegeId: string;
  collegeName: string;
  completed: number;
  total: number;
}

/**
 * Get detailed supplement progress by college for the dashboard
 */
export async function getDetailedSupplementsProgress(studentId: string): Promise<SupplementProgress[]> {
  // 1. Get Favorites
  try {
    // 1. Get Favorites AND Started Essays to track all relevant colleges
    const { data: favorites, error: favError } = await supabase
      .from("student_shortlists")
      .select("college_id")
      .eq("student_id", studentId);

    if (favError) throw favError;

    // Only use favorites - supplements dashboard should only show favorited colleges
    const collegeIds = favorites?.map(f => String(f.college_id)) || [];

    if (collegeIds.length === 0) return [];

    // 2. Get College Names
    const { data: colleges, error: colError } = await supabase
      .from("location_type_of_institution")
      .select("csc_unitid, csc_instnm")
      .in("csc_unitid", collegeIds);

    if (colError) throw colError;
    if (!colleges) return [];

    // Map ID -> Name and init stats
    const collegeMap = new Map<string, string>();
    const stats = new Map<string, { completed: number; total: number }>();

    colleges.forEach(c => {
      const id = String(c.csc_unitid);
      collegeMap.set(id, c.csc_instnm);
      stats.set(id, { completed: 0, total: 0 });
    });

    const collegeNames = Array.from(collegeMap.values());

    // 3. Get Total Prompts (by Name)
    // Note: This relies on college names matching exactly between tables
    const { data: prompts, error: promptError } = await supabase
      .from("college_supplemental_essays")
      .select("college_name")
      .in("college_name", collegeNames);

    if (promptError) console.error("Error fetching prompts for progress:", promptError);

    // Count prompts per college name -> map to ID
    if (prompts) {
      prompts.forEach(p => {
        for (const [id, name] of collegeMap.entries()) {
          if (name === p.college_name) {
            const s = stats.get(id)!;
            s.total++;
          }
        }
      });
    }

    // 4. Get Completed Essays (by ID)
    const { data: completedEssays, error: essayError } = await supabase
      .from("essays")
      .select("college_id")
      .eq("student_id", studentId)
      .eq("essay_type_id", 3)
      .eq("is_complete", true);

    if (essayError) console.error("Error fetching completed essays:", essayError);

    if (completedEssays) {
      completedEssays.forEach(e => {
        const id = String(e.college_id);
        if (stats.has(id)) {
          const s = stats.get(id)!;
          s.completed++;
        }
      });
    }

    // Format
    return Array.from(stats.entries()).map(([id, s]) => ({
      collegeId: id,
      collegeName: collegeMap.get(id) || "Unknown",
      completed: s.completed,
      total: s.total
    }));

  } catch (error) {
    console.error("🔧 [API] getDetailedSupplementsProgress error:", error);
    return [];
  }
}

/**
 * Get IDs of completed supplements for a specific college
 * Returns college_prompt_ids (e.g. "princeton_1")
 */
export async function getCompletedSupplementIds(studentId: string, collegeId: string): Promise<string[]> {
  try {
    // 1. Get completed essays (prompt_id is UUID)
    const { data: essays, error } = await supabase
      .from("essays")
      .select("prompt_id")
      .eq("student_id", studentId)
      .eq("college_id", collegeId)
      .eq("essay_type_id", 3)
      .eq("is_complete", true);

    if (error) throw error;
    if (!essays || essays.length === 0) return [];

    const promptUuids = essays.map(e => e.prompt_id).filter(Boolean); // Filter nulls

    if (promptUuids.length === 0) return [];

    // 2. Get college_prompt_id strings corresponding to these UUIDs
    const { data: prompts, error: promptError } = await supabase
      .from("college_supplemental_essays")
      .select("college_prompt_id")
      .in("id", promptUuids);

    if (promptError) throw promptError;

    return prompts?.map(p => p.college_prompt_id) || [];
  } catch (error) {
    console.error("🔧 [API] getCompletedSupplementIds error:", error);
    return [];
  }
}

// ==========================================
// DASHBOARD HELPERS
// ==========================================

export async function getMostRecentDraft(studentId: string) {
  // 1. Get student's essays (Common App and Personal Statement only)
  const { data: essays, error: essayError } = await supabase
    .from("essays")
    .select("id, is_starred")
    .eq("student_id", studentId)
    .in("essay_type_id", [1, 2]); // Filter for Common App (1) and Personal Statement (2)

  if (essayError) throw essayError;
  if (!essays || essays.length === 0) return null;

  // 2. Prioritize starred essays
  const starredEssays = essays.filter(e => e.is_starred);
  const essaysToSearch = starredEssays.length > 0 ? starredEssays : essays;
  const essayIds = essaysToSearch.map(e => e.id);

  // 3. First, try to find a draft with actual content (word_count > 0)
  // This avoids showing newly created blank drafts over edited ones
  const { data: draftWithContent, error: contentError } = await supabase
    .from("essay_drafts")
    .select("*, essays(title, updated_at, is_starred)")
    .in("essay_id", essayIds)
    .gt("word_count", 0)
    .order("updated_at", { ascending: false })
    .limit(1)
    .single();

  if (!contentError && draftWithContent) {
    return draftWithContent;
  }

  // 4. Fallback: if no drafts with content, get the most recent draft (even if blank)
  const { data: draft, error: draftError } = await supabase
    .from("essay_drafts")
    .select("*, essays(title, updated_at, is_starred)")
    .in("essay_id", essayIds)
    .order("updated_at", { ascending: false })
    .limit(1)
    .single();

  if (draftError) {
    if (draftError.code === "PGRST116") return null; // No drafts found
    throw draftError;
  }

  return draft;
}
